"use client";

import Link from "next/link";
import { Wordmark } from "@/components/Logo";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

// ─────────────────────────────────────────────────────────────────────────────
// SiteHeaderClient — the interactive half of the site header: the mobile
// hamburger and its panel. It is TOLD whether the visitor is signed in; it
// does not look. The server half lives in components/SiteHeader.tsx.
//   Logged out: logo → homepage · Templates · Pricing · Login · Build Free
//   Logged in:  logo → DASHBOARD · Templates · Pricing · Dashboard · Build Free
// (Founder's issue #1: "once I am login, home means dashboard.")
// Rendered only by the pages that ask for it — never by the root layout.
// ─────────────────────────────────────────────────────────────────────────────

const LINK = "text-stone-600 transition hover:text-stone-900";

export function SiteHeaderClient({ loggedIn }: { loggedIn: boolean }) {
  const pathname = usePathname() ?? "/";
  // mobile: the three site links fold into a hamburger instead of vanishing
  // (founder, 5 Aug 2026: responsive with a hamburger, not links that hide)
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => setMenuOpen(false), [pathname]);

  // NO PATHNAME GUARD, DELIBERATELY (Buds, 5 Aug 2026, after the double
  // header came back on production a second time). This component used to
  // decide for itself whether it belonged on the page, by reading
  // usePathname() at render time — and Vercel's ISR regeneration made that
  // guard miss. A page cannot get a header it never asked for: the root
  // layout renders none, and the pages that want one import it.
  //
  // AND IT NO LONGER GUESSES AT THE SESSION. `loggedIn` used to be read from
  // the cookie in an effect, so the first paint always said "Login" and swapped
  // to "Dashboard" ~200ms later — measured. A customer clicking Pricing from
  // his dashboard saw "Login" flash and asked, reasonably, whether he had been
  // logged out (founder, 5 Aug 2026). The server knows the answer before it
  // renders; it passes it in.

  return (
    <header className="sticky top-0 z-40 border-b border-stone-200 bg-white/90 backdrop-blur relative">
      <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3.5 sm:gap-6 sm:px-6">
        {/* The logo is the way home, signed in or not (founder, 5 Aug 2026).
            Sending a logged-in visitor to the dashboard means there is no way
            back to the website he is reading. */}
        <Link href="/" className="text-stone-900 no-underline">
          <Wordmark />
        </Link>
        <nav className="ml-auto flex items-center gap-3 text-sm sm:gap-5">
          {/* Same menu as the homepage's nav, item for item — one website, one
              menu (founder, 5 Aug 2026). Anchors are absolute so they work from
              a page that is not home. On mobile these fold into the hamburger. */}
          <Link href="/#templates" className={`${LINK} hidden sm:inline`}>Templates</Link>
          <Link href="/#how" className={`${LINK} hidden sm:inline`}>How it works</Link>
          <Link href="/pricing" className={`${LINK} hidden sm:inline`}>Pricing</Link>
          {/* On a phone the row is logo + Build Free + menu; Login/Dashboard
              moves into the panel (360px pass, 20 Sep 2026: the row ran 70px
              past the screen on every marketing page). */}
          {loggedIn ? (
            <Link href="/dashboard" className={`${LINK} hidden sm:inline`}>Dashboard</Link>
          ) : (
            <Link href="/login" className={`${LINK} hidden sm:inline`}>Login</Link>
          )}
          <Link
            href={loggedIn ? "/create" : "/register?next=/create"}
            className="rounded-lg bg-rose-600 px-3.5 py-2 text-sm font-semibold text-white no-underline transition hover:bg-rose-700"
          >
            Build Free →
          </Link>
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="site-mobile-nav"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-stone-300 text-stone-700 sm:hidden"
          >
            {menuOpen ? (
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path d="M3 3l12 12M15 3L3 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 14" aria-hidden="true">
                <path d="M1 1h16M1 7h16M1 13h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </nav>
      </div>

      {menuOpen && (
        <nav
          id="site-mobile-nav"
          style={{ left: 0, right: 0, width: "100%" }}
          className="absolute inset-x-0 top-full z-40 w-full border-b border-stone-200 bg-white px-6 pb-3 shadow-sm sm:hidden"
        >
          <Link href="/#templates" onClick={() => setMenuOpen(false)} className="block border-t border-stone-100 py-3 text-base font-medium text-stone-700 first:border-t-0">Templates</Link>
          <Link href="/#how" onClick={() => setMenuOpen(false)} className="block border-t border-stone-100 py-3 text-base font-medium text-stone-700">How it works</Link>
          <Link href="/pricing" onClick={() => setMenuOpen(false)} className="block border-t border-stone-100 py-3 text-base font-medium text-stone-700">Pricing</Link>
          {loggedIn ? (
            <Link href="/dashboard" onClick={() => setMenuOpen(false)} className="block border-t border-stone-100 py-3 text-base font-medium text-stone-700">Dashboard</Link>
          ) : (
            <Link href="/login" onClick={() => setMenuOpen(false)} className="block border-t border-stone-100 py-3 text-base font-medium text-stone-700">Login</Link>
          )}
        </nav>
      )}
    </header>
  );
}
